import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { decodeIdLong as decodeId } from "../../lib/urlId";
import PageService from "./PageService";
import PeppaSpinner from "../../components/spinners/PeppaSpinner";
import DOMPurify from "dompurify";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { Info, FileText, Wallet, ListChecks, FilePlus2, MapPin, CalendarClock, ExternalLink, Phone, CheckCircle2, ArrowLeft } from "lucide-react";
import { trackEvent } from "../../lib/matomo";
import peppaLogo from "../../assets/icons/peppa_logo_blue.svg";
import { RouterPath } from "../../assets/dictionary/RouterPath";

export default function PageAide() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    const decoded = decodeId(id) ?? id;
    setIsLoading(true);
    setError(null);
    PageService.getPageById(decoded)
      .then((res) => {
        setData(res.data);
        try { if (res?.data?.name) trackEvent("aide", "view", String(res.data.name).slice(0,150)); } catch(_) {}
      })
      .catch((e) => {
        setError("Impossible de charger l'aide demandée.");
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  // legacy computed fields (plus utilisé mais conservé pour référence)
  // const fields = useMemo(() => {
  //   if (!data || typeof data !== "object") return [];
  //   const preferredOrder = [
  //     "name",
  //     "description",
  //     "eligibility",
  //     "operation",
  //     "structure_eligible",
  //     "localite",
  //     "financiers",
  //     "link",
  //     "link_application",
  //     "type_of_call",
  //     "submission_deadline",
  //     "contact",
  //     "annexe",
  //     "mention_legal",
  //   ];
  //   const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== "");
  //   entries.sort((a, b) => {
  //     const ia = preferredOrder.indexOf(a[0]);
  //     const ib = preferredOrder.indexOf(b[0]);
  //     if (ia === -1 && ib === -1) return a[0].localeCompare(b[0]);
  //     if (ia === -1) return 1;
  //     if (ib === -1) return -1;
  //     return ia - ib;
  //   });
  //   return entries;
  // }, [data]);

  const isLikelyHtml = (str) => typeof str === "string" && /<\/?[a-z][\s\S]*>/i.test(str);

  const friendlyLabel = (key) =>
    key
      .replaceAll("_", " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // Build grouped sections with hierarchy inspired by modern aid detail pages
  const grouped = useMemo(() => {
    if (!data) return null;

    // Normalize helper
    const pick = (k) => data[k];
    const pickAny = (...keys) => keys.map((k) => data[k]).find((v) => v !== undefined && v !== null && v !== "");

    const main = {
      resume: pickAny("resume", "summary"),
      presentation: pick("description"),
      amount: pickAny("amount", "montant", "montant_aide"),
      eligibility: pick("eligibility"),
      operations: pick("operation"),
      structures: pick("structure_eligible"),
      annex: pick("annexe"),
      mention: pick("mention_legal"),
    };

    const side = {
      type: pick("type_of_call"),
      area: pick("localite"),
      financiers: pick("financiers"),
      deadline: pick("submission_deadline"),
      link: pick("link"),
      linkApplication: pick("link_application"),
      contact: pick("contact"),
    };

    // Unknown fields fallback
    const knownKeys = new Set([
      "name","description","eligibility","operation","structure_eligible","localite","financiers","link","link_application","type_of_call","submission_deadline","contact","annexe","mention_legal","resume","summary","amount","montant","montant_aide", "partner"
    ]);
    const others = Object.entries(data)
      .filter(([k, v]) => !knownKeys.has(k) && v !== null && v !== undefined && v !== "")
      .sort(([a],[b]) => a.localeCompare(b));

    return { main, side, others };
  }, [data]);

  const formatFrDate = (value) => {
    try {
      const d = new Date(String(value));
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "2-digit" });
    } catch (_) {
      return String(value);
    }
  };

  const daysRemaining = (value) => {
    try {
      const d = new Date(String(value));
      if (Number.isNaN(d.getTime())) return null;
      const ms = d.setHours(0,0,0,0) - new Date().setHours(0,0,0,0);
      return Math.ceil(ms / (1000 * 60 * 60 * 24));
    } catch (_) { return null; }
  };

  // Build adaptive TOC
  const tocItems = useMemo(() => {
    const items = [];
    if (grouped?.main?.resume) items.push({ id: "resume", label: "Résumé", icon: Info });
    if (data?.description) items.push({ id: "presentation", label: "Présentation", icon: FileText });
    if (grouped?.main?.amount) items.push({ id: "montant", label: "Montant", icon: Wallet });
    if (grouped?.main?.eligibility || grouped?.main?.structures || grouped?.main?.operations) {
      items.push({ id: "conditions", label: "Conditions", icon: ListChecks });
      if (grouped?.main?.eligibility) items.push({ id: "conditions-beneficiaires", label: "Bénéficiaires", icon: CheckCircle2 });
      if (grouped?.main?.operations) items.push({ id: "conditions-operations", label: "Opérations", icon: FilePlus2 });
      if (grouped?.main?.structures) items.push({ id: "conditions-structures", label: "Structures éligibles", icon: ListChecks });
    }
    if (grouped?.others && grouped.others.length > 0) items.push({ id: "autres", label: "Autres", icon: FileText });
    return items;
  }, [grouped, data]);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 100; // safe zone
    window.scrollTo({ top: y, behavior: 'smooth' });
    el.classList.add('ring-2','ring-primary/50','rounded-md');
    window.setTimeout(() => {
      el.classList.remove('ring-2','ring-primary/50','rounded-md');
    }, 1200);
  }, []);

  const handleBackClick = () => {
    const from = location.state && location.state.from;
    const fromPath = typeof from?.pathname === "string" ? from.pathname : null;

    if (fromPath && fromPath.startsWith(RouterPath.CHAT)) {
      navigate(-1);
    } else {
      navigate(RouterPath.CHAT);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="max-w-5xl mx-auto p-4">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-center">
                <PeppaSpinner />
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-5xl mx-auto p-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="max-w-5xl mx-auto p-4">
          <Alert>
            <AlertDescription>Aucune donnée à afficher.</AlertDescription>
          </Alert>
        </div>
      );
    }

    return (
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Header */}
        <Card>
          <CardHeader className="space-y-2">
            <CardTitle id="titre" className="text-2xl">
              {data.name || "Détail de l'aide"}
            </CardTitle>
            <div className="flex flex-wrap gap-2 items-center">
              {grouped?.side?.type && (
                <Badge variant="secondary" className="break-words whitespace-normal leading-snug">
                  {String(grouped.side.type)}
                </Badge>
              )}
              {grouped?.side?.area && (
                Array.isArray(grouped.side.area) ? (
                  <div className="flex flex-wrap gap-2">
                    {grouped.side.area.map((loc, i) => (
                      <Badge key={i} variant="outline" className="rounded-md bg-primary/10 text-primary border-primary/20 break-words">
                        <MapPin className="h-3 w-3 mr-1" />{String(loc)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 max-w-full">
                    {String(grouped.side.area).split(",").map((loc, i) => (
                      <Badge key={i} variant="outline" className="rounded-md bg-primary/10 text-primary border-primary/20 break-words">
                        <MapPin className="h-3 w-3 mr-1" />{loc.trim()}
                      </Badge>
                    ))}
                  </div>
                )
              )}
              {grouped?.side?.deadline && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" />
                  <span>Date limite: {formatFrDate(grouped.side.deadline)}</span>
                </Badge>
              )}
              {(() => { const r = daysRemaining(grouped?.side?.deadline); if (r !== null && r <= 60 && r >= 0) { return (
                <Badge className={cn("flex items-center gap-1", r <= 7 ? "bg-destructive text-destructive-foreground" : r <= 30 ? "bg-amber-500/15 text-amber-700" : "bg-primary/10 text-primary")}>
                  <Info className="h-3 w-3" />
                  Plus que {r} jour{r>1?'s':''}
                </Badge>
              ); } return null; })()}
            </div>
          </CardHeader>
        </Card>

        {/* Mobile TOC under header */}
        {tocItems.length > 0 && (
          <Card className="md:hidden">
            <CardHeader className="pb-2"><CardTitle className="text-lg">Sommaire</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <ul className="space-y-2">
                {tocItems.map(({ id, label, icon: Icon }) => (
                  <li key={id}>
                    <button onClick={() => scrollToSection(id)} className="text-primary hover:underline flex items-center gap-2">
                      <Icon className="h-4 w-4" />{label}
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Main column */}
          <div className="md:col-span-8 space-y-4">
            {grouped?.main?.resume && (
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center gap-2"><Info className="h-4 w-4 text-primary" /><CardTitle className="text-lg" id="resume">Résumé</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{String(grouped.main.resume)}</p>
                </CardContent>
              </Card>
            )}

            {grouped?.main?.presentation ?? grouped?.main?.presentation !== undefined ? (
              data.description ? (
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center gap-2"><FileText className="h-4 w-4 text-primary" /><CardTitle className="text-lg" id="presentation">Présentation</CardTitle></CardHeader>
                  <CardContent>
                    <div className="prose dark:prose-invert max-w-none text-sm">
                      {isLikelyHtml(data.description) ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(data.description)) }} />
                      ) : (
                        <p className="text-muted-foreground">{String(data.description)}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : null
            ) : null}

            {grouped?.main?.amount && (
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center gap-2"><Wallet className="h-4 w-4 text-primary" /><CardTitle className="text-lg" id="montant">Montant de l'aide</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm">{String(grouped.main.amount)}</p>
                </CardContent>
              </Card>
            )}

            {(grouped?.main?.eligibility || grouped?.main?.structures || grouped?.main?.operations) && (
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /><CardTitle className="text-lg" id="conditions">Conditions d'obtention</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {grouped.main.eligibility && (
                    <div>
                      <div id="conditions-beneficiaires" className="font-medium mb-2 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" />Bénéficiaires</div>
                      <div className="text-sm">
                        {Array.isArray(grouped.main.eligibility) ? (
                          <div className="flex flex-wrap gap-2">
                            {grouped.main.eligibility.map((v, i) => (
                              <Badge key={i} variant="outline" className="rounded-md bg-primary/5 text-foreground border-primary/15">
                                {String(v)}
                              </Badge>
                            ))}
                          </div>
                        ) : isLikelyHtml(grouped.main.eligibility) ? (
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(grouped.main.eligibility)) }} />
                        ) : (
                          <p>{String(grouped.main.eligibility)}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {grouped.main.operations && (
                    <div>
                      <div id="conditions-operations" className="font-medium mb-1 flex items-center gap-2"><FilePlus2 className="h-4 w-4 text-primary" />Type d'opérations</div>
                      <div className="text-sm">
                        {Array.isArray(grouped.main.operations) ? (
                          <ul className="list-disc pl-5 space-y-1">
                            {grouped.main.operations.map((v, i) => <li key={i}>{String(v)}</li>)}
                          </ul>
                        ) : isLikelyHtml(grouped.main.operations) ? (
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(grouped.main.operations)) }} />
                        ) : (
                          <p>{String(grouped.main.operations)}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {grouped.main.structures && (
                    <div>
                      <div id="conditions-structures" className="font-medium mb-1 flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" />Structures éligibles</div>
                      <div className="text-sm">
                        {Array.isArray(grouped.main.structures) ? (
                          <ul className="list-disc pl-5 space-y-1">
                            {grouped.main.structures.map((v, i) => <li key={i}>{String(v)}</li>)}
                          </ul>
                        ) : isLikelyHtml(grouped.main.structures) ? (
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(grouped.main.structures)) }} />
                        ) : (
                          <p>{String(grouped.main.structures)}</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {(grouped?.main?.annex || grouped?.main?.mention) && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-lg">Informations complémentaires</CardTitle></CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {grouped.main.annex && (
                    <div>
                      <div className="font-medium mb-1">Annexe</div>
                      {isLikelyHtml(grouped.main.annex) ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(grouped.main.annex)) }} />
                      ) : (
                        <p>{String(grouped.main.annex)}</p>
                      )}
                    </div>
                  )}
                  {grouped.main.mention && (
                    <div>
                      <div className="font-medium mb-1">Mention légale</div>
                      {isLikelyHtml(grouped.main.mention) ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(grouped.main.mention)) }} />
                      ) : (
                        <p>{String(grouped.main.mention)}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Fallback unknown fields */}
            {grouped?.others && grouped.others.length > 0 && (
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center gap-2"><FileText className="h-4 w-4 text-primary" /><CardTitle className="text-lg" id="autres">Autres informations</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 gap-3">
                  {grouped.others.map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <div className="font-medium text-sm">{friendlyLabel(key)}</div>
                      <div className="text-sm text-foreground/90">
                        {Array.isArray(value) ? (
                          <ul className="list-disc pl-5 space-y-1">{value.map((v,i)=>(<li key={i}>{String(v)}</li>))}</ul>
                        ) : typeof value === 'object' ? (
                          <pre className="text-xs whitespace-pre-wrap bg-muted rounded-md p-3">{JSON.stringify(value, null, 2)}</pre>
                        ) : isLikelyHtml(value) ? (
                          <div className="prose dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(value)) }} />
                        ) : (
                          <span>{String(value)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Side column */}
          <div className="md:col-span-4 space-y-4 md:sticky md:top-4 h-fit">
            {/* Partenaire advertisement */}
            {data.partner?.description && (
              <Card className="hidden md:block">
                <CardHeader className="pb-2"><CardTitle className="text-lg">Besoin d'aide pour votre dossier ?</CardTitle></CardHeader>
                <CardContent className="text-sm">
                    {data.partner.logo && (
                      <img
                        src={data.partner.logo}
                        alt={data.partner.name ?? "Partner logo"}
                        className="mb-2 h-10 object-contain"
                      />
                    )}
                  <p dangerouslySetInnerHTML={{ __html: data.partner.description ?? "" }} />
                  <Button asChild variant="default" className="w-full mt-2">
                    <a href={data.partner.link} target="_self" rel="noreferrer" className="inline-flex items-center gap-2">
                      Vérifier gratuitement
                    </a>
                  </Button>
                </CardContent>
              </Card>
            )}
            {/* Table of contents */}
            {tocItems.length > 0 && (
              <Card className="hidden md:block">
                <CardHeader className="pb-2"><CardTitle className="text-lg">Sommaire</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  <ul className="space-y-2">
                    {tocItems.map(({ id, label, icon: Icon }) => (
                      <li key={id}>
                        <button onClick={() => scrollToSection(id)} className="text-primary hover:underline flex items-center gap-2">
                          <Icon className="h-4 w-4" />{label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-lg">En résumé</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {grouped?.side?.type && (
                    <Badge variant="secondary" className="break-words whitespace-normal leading-snug">
                      {String(grouped.side.type)}
                    </Badge>
                  )}
                  {grouped?.side?.area && (
                    Array.isArray(grouped.side.area) ? (
                      <div className="flex flex-wrap gap-2">
                        {grouped.side.area.map((loc, i) => (
                          <Badge key={i} variant="outline" className="rounded-md bg-primary/10 text-primary border-primary/20 break-words">
                            <MapPin className="h-3 w-3 mr-1" />{String(loc)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 max-w-full">
                        {String(grouped.side.area).split(",").map((loc, i) => (
                          <Badge key={i} variant="outline" className="rounded-md bg-primary/10 text-primary border-primary/20 break-words">
                            <MapPin className="h-3 w-3 mr-1" />{loc.trim()}
                          </Badge>
                        ))}
                      </div>
                    )
                  )}
                </div>
                {grouped?.side?.financiers && (
                  <div className="text-sm">
                    <div className="font-medium mb-1">Financeur(s)</div>
                    {Array.isArray(grouped.side.financiers) ? (
                      <ul className="list-disc pl-5 space-y-1">{grouped.side.financiers.map((v,i)=>(<li key={i} className="break-words">{String(v)}</li>))}</ul>
                    ) : (
                      <p className="break-words whitespace-pre-wrap">{String(grouped.side.financiers)}</p>
                    )}
                  </div>
                )}

                {grouped?.side?.deadline && (
                  <div className="text-sm">
                    <div className="font-medium mb-1">Date limite</div>
                    <p>{formatFrDate(grouped.side.deadline)}</p>
                    {(() => { const r = daysRemaining(grouped.side.deadline); if (r !== null && r <= 60 && r >= 0) { return (
                      <div className={cn("mt-1 text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md", r <= 7 ? "bg-destructive text-destructive-foreground" : r <= 30 ? "bg-amber-500/15 text-amber-700" : "bg-primary/10 text-primary")}>
                        <CalendarClock className="h-3 w-3" />
                        Plus que {r} jour{r>1?'s':''}
                      </div>
                    ); } return null; })()}
                  </div>
                )}

                {(grouped?.side?.link || grouped?.side?.linkApplication) && (
                  <div className="flex flex-col gap-2">
                    {grouped.side.link && (
                      <Button asChild variant="default">
                        <a href={String(grouped.side.link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
                          <ExternalLink className="h-4 w-4" />Voir la page officielle
                        </a>
                      </Button>
                    )}
                    {grouped.side.linkApplication && (
                      <Button asChild variant="secondary">
                        <a href={String(grouped.side.linkApplication)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
                          <FilePlus2 className="h-4 w-4" />Déposer une demande
                        </a>
                      </Button>
                    )}
                  </div>
                )}

                {grouped?.side?.contact && (
                  <div className="text-sm">
                    <div className="font-medium mb-1">Contact</div>
                    {isLikelyHtml(grouped.side.contact) ? (
                      <div className="prose dark:prose-invert max-w-none break-words" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(grouped.side.contact)) }} />
                    ) : (
                      <p className="break-words whitespace-pre-wrap inline-flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5" />{String(grouped.side.contact)}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen relative app-page-gradient flex-col">
      <header className="w-full border-b border-border/20 bg-card/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-4 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="hover:bg-accent/50 text-foreground inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span>Retour au chat</span>
          </Button>

          <div className="flex items-center">
            <img
              src={peppaLogo}
              alt="PeppaCorp"
              className="h-8 w-auto sm:h-9 rounded-sm"
            />
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        {renderContent()}
      </main>
    </div>
  );
} 